import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Send, GitPullRequest, Clock } from "lucide-react";

const TEXT_FIELDS = [
  ["intake_phone", "Central intake phone"],
  ["after_hours_phone", "After-hours phone"],
  ["admission_hours", "Admission hours"],
  ["email", "Operational email"],
  ["website", "Website"],
];

export default function ProviderProposals() {
  const [programs, setPrograms] = useState([]);
  const [payers, setPayers] = useState([]);
  const [selId, setSelId] = useState("");
  const [detail, setDetail] = useState(null);
  const [form, setForm] = useState({});
  const [weekend, setWeekend] = useState("");
  const [transport, setTransport] = useState("");
  const [payerState, setPayerState] = useState({});
  const [note, setNote] = useState("");
  const [reqs, setReqs] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadReqs = () => api.get("/provider/change-requests").then((r) => setReqs(r.data));
  useEffect(() => {
    api.get("/admin/programs").then((r) => setPrograms(r.data));
    api.get("/payers").then((r) => setPayers(r.data));
    loadReqs();
  }, []);

  const selectProgram = async (id) => {
    setSelId(id);
    setNote("");
    if (!id) { setDetail(null); return; }
    const { data } = await api.get(`/programs/${id}`);
    setDetail(data);
    setForm({
      intake_phone: data.intake_phone || "", after_hours_phone: data.after_hours_phone || "",
      admission_hours: data.admission_hours || "", email: data.email || "", website: data.website || "",
    });
    setWeekend(data.weekend_process || "");
    setTransport(data.transportation?.status || "");
    const ps = {};
    (data.payers || []).forEach((p) => { ps[p.payer] = { contracted: !!p.contracted, accepting: !!p.accepting }; });
    setPayerState(ps);
  };

  const togglePayer = (name, key) =>
    setPayerState((s) => ({ ...s, [name]: { contracted: false, accepting: false, ...(s[name] || {}), [key]: !(s[name]?.[key]) } }));

  const submit = async () => {
    if (!selId) return;
    const changes = { ...form, weekend_process: weekend };
    changes.transportation = { ...(detail.transportation || {}), status: transport };
    changes.payers = Object.entries(payerState)
      .filter(([, v]) => v.contracted || v.accepting)
      .map(([payer, v]) => ({ payer, contracted: v.contracted, accepting: v.accepting }));
    setBusy(true);
    try {
      await api.post("/provider/change-requests", { program_id: selId, changes, note });
      toast.success("Proposed changes submitted for LATP approval.");
      setNote("");
      loadReqs();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const inputCls = "w-full rounded-lg border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-teal-600";

  return (
    <div className="mx-auto max-w-4xl">
      <h1 className="font-display text-3xl font-extrabold tracking-tight text-slate-900">Propose changes</h1>
      <p className="mt-1 text-sm text-slate-500">
        Submit updates to facility information, accepted insurance, and transportation. Changes take effect only after LATP approves them.
      </p>

      <div className="mt-5 rounded-xl border border-slate-200 bg-white p-5">
        <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-500">Select a program</label>
        <select data-testid="proposal-program-select" className={inputCls} value={selId} onChange={(e) => selectProgram(e.target.value)}>
          <option value="">Choose a program…</option>
          {programs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>

        {detail && (
          <div className="mt-5 space-y-5">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {TEXT_FIELDS.map(([k, label]) => (
                <div key={k}>
                  <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-500">{label}</label>
                  <input data-testid={`proposal-field-${k}`} className={inputCls} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
                </div>
              ))}
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-500">Transportation status</label>
                <select data-testid="proposal-transport" className={inputCls} value={transport} onChange={(e) => setTransport(e.target.value)}>
                  <option value="">Unset</option>
                  <option value="available_now">Available now</option>
                  <option value="available_with_notice">Available with notice</option>
                  <option value="limited">Limited</option>
                  <option value="unavailable">Unavailable</option>
                </select>
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-500">Weekend & after-hours process</label>
              <textarea data-testid="proposal-field-weekend_process" rows={2} className={inputCls} value={weekend} onChange={(e) => setWeekend(e.target.value)} />
            </div>

            <div>
              <div className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-500">Accepted insurance</div>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {payers.map((p) => {
                  const st = payerState[p.name] || {};
                  return (
                    <div key={p.name} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2 text-sm">
                      <span className="text-slate-700">{p.name}</span>
                      <div className="flex items-center gap-3 text-xs">
                        <label className="flex items-center gap-1"><input type="checkbox" data-testid={`payer-${p.name}-contracted`} checked={!!st.contracted} onChange={() => togglePayer(p.name, "contracted")} /> Contracted</label>
                        <label className="flex items-center gap-1"><input type="checkbox" data-testid={`payer-${p.name}-accepting`} checked={!!st.accepting} onChange={() => togglePayer(p.name, "accepting")} /> Accepting</label>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-slate-500">Note to LATP (optional)</label>
              <input data-testid="proposal-note" className={inputCls} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Reason for the change" />
            </div>

            <button data-testid="submit-proposal-btn" onClick={submit} disabled={busy} className="flex w-full items-center justify-center gap-2 rounded-xl bg-teal-700 py-3.5 text-base font-bold text-white hover:bg-teal-800 disabled:opacity-60">
              <Send size={18} /> Submit for LATP approval
            </button>
          </div>
        )}
      </div>

      <div className="mt-6">
        <h2 className="font-display text-xl font-bold text-slate-900">Your submitted requests</h2>
        <div className="mt-3 space-y-2">
          {reqs.length === 0 && <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">No change requests submitted yet.</div>}
          {reqs.map((r) => (
            <div key={r.id} data-testid={`my-change-req-${r.id}`} className="flex items-center justify-between rounded-xl border border-slate-200 bg-white p-4">
              <div>
                <div className="font-semibold text-slate-900">{r.program_name}</div>
                <div className="flex items-center gap-1 text-xs text-slate-500"><Clock size={12} /> {new Date(r.created_at).toLocaleString()}</div>
              </div>
              <span className={`rounded-md px-2.5 py-1 text-xs font-semibold capitalize ${r.status === "pending" ? "bg-amber-50 text-amber-700" : r.status === "approved" ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>{r.status}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
